import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import Header from "@/components/app/Header";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Shield, UserPlus, Pencil, Key, Power, ArrowClockwise, User, Briefcase, FolderSimple, ArrowsClockwise, AddressBook, Trash, Sparkle, FolderPlus, Eye, DotsThreeVertical,
} from "@phosphor-icons/react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import ClientProfileDialog from "@/components/app/ClientProfileDialog";

const empty = { email: "", password: "", name: "", role: "solicitor", is_admin: false, phone: "", office_address: "", user_type: "solicitor" };

const USER_TYPE_OPTIONS = [
  { value: "solicitor", label: "Solicitor", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "client_onboarding", label: "Client Onboarding", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "completion_team", label: "Completion Team", color: "bg-orange-50 text-orange-700 border-orange-200" },
];
const userTypeLabel = (k) => USER_TYPE_OPTIONS.find((o) => o.value === k)?.label || "Solicitor";
const userTypeColor = (k) => USER_TYPE_OPTIONS.find((o) => o.value === k)?.color || USER_TYPE_OPTIONS[0].color;

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("solicitor");
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(empty);
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "", office_address: "", is_admin: false, out_of_office_enabled: false, cover_solicitor_id: "", user_type: "solicitor" });
  const [pwOpen, setPwOpen] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [busy, setBusy] = useState(false);

  // Caseload state
  const [caseload, setCaseload] = useState([]);
  const [caseloadLoading, setCaseloadLoading] = useState(false);

  // Clients tab — enriched list with cases_count + cases[]. Lazily fetched
  // when the user lands on the Clients tab. View-profile drawer state below.
  const [clientsList, setClientsList] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [viewClientId, setViewClientId] = useState(null);

  // Client leads (contact-only — clients without an account yet)
  const emptyLead = { name: "", email: "", phone: "", address: "", notes: "" };
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadDialogOpen, setLeadDialogOpen] = useState(false);
  const [leadForm, setLeadForm] = useState(emptyLead);
  const [editingLeadId, setEditingLeadId] = useState(null);
  const [savingLead, setSavingLead] = useState(false);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [promoteLead, setPromoteLead] = useState(null);
  const [promotePassword, setPromotePassword] = useState("");
  const [promoting, setPromoting] = useState(false);

  // Create-Case-from-Lead dialog
  const [caseDialogOpen, setCaseDialogOpen] = useState(false);
  const [caseLead, setCaseLead] = useState(null);
  const [caseForm, setCaseForm] = useState({ title: "", property_address: "", case_type: "buying" });
  const [creatingCase, setCreatingCase] = useState(false);
  const [selectedFromSolicitor, setSelectedFromSolicitor] = useState("");
  const [selectedCaseIds, setSelectedCaseIds] = useState(new Set());
  const [reassignOpen, setReassignOpen] = useState(false);
  const [destSolicitor, setDestSolicitor] = useState("");
  const [disableCoverForSource, setDisableCoverForSource] = useState(false);

  // Bounce non-admins away
  useEffect(() => {
    if (me === false) navigate("/login");
    else if (me && !me.is_admin) {
      toast.error("Admin access required");
      navigate("/");
    }
  }, [me, navigate]);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await API.get("/admin/users");
      setUsers(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (me?.is_admin) fetchUsers(); }, [fetchUsers, me]);

  const fetchCaseload = useCallback(async () => {
    setCaseloadLoading(true);
    try {
      const { data } = await API.get("/admin/caseload");
      setCaseload(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load caseload");
    } finally {
      setCaseloadLoading(false);
    }
  }, []);

  // Lazily fetch the enriched clients list (with cases_count + cases[]) when
  // the user lands on the Clients tab. Reuse on every visit so newly-added
  // cases show their new client linkage without a hard refresh.
  const fetchClients = useCallback(async () => {
    setClientsLoading(true);
    try {
      const { data } = await API.get("/admin/clients");
      setClientsList(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load clients");
    } finally {
      setClientsLoading(false);
    }
  }, []);
  useEffect(() => {
    if (tab === "client" && me?.is_admin) fetchClients();
  }, [tab, me?.is_admin, fetchClients]);

  useEffect(() => { if (me?.is_admin && tab === "caseload") fetchCaseload(); }, [tab, me, fetchCaseload]);

  const fetchLeads = useCallback(async () => {
    setLeadsLoading(true);
    try {
      const { data } = await API.get("/admin/client-leads");
      setLeads(data);
    } catch {
      toast.error("Failed to load client leads");
    } finally {
      setLeadsLoading(false);
    }
  }, []);

  useEffect(() => { if (me?.is_admin && tab === "leads") fetchLeads(); }, [tab, me, fetchLeads]);

  const openCreateLead = () => { setEditingLeadId(null); setLeadForm(emptyLead); setLeadDialogOpen(true); };
  const openEditLead = (lead) => {
    setEditingLeadId(lead.id);
    setLeadForm({
      name: lead.name || "", email: lead.email || "", phone: lead.phone || "",
      address: lead.address || "", notes: lead.notes || "",
    });
    setLeadDialogOpen(true);
  };
  const saveLead = async () => {
    if (!leadForm.name.trim()) { toast.error("Name is required"); return; }
    setSavingLead(true);
    try {
      if (editingLeadId) {
        await API.put(`/admin/client-leads/${editingLeadId}`, leadForm);
        toast.success("Lead updated");
      } else {
        await API.post("/admin/client-leads", leadForm);
        toast.success("Lead added");
      }
      setLeadDialogOpen(false);
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingLead(false);
    }
  };
  const deleteLead = async (lead) => {
    if (!window.confirm(`Delete lead "${lead.name}"? This won't affect any cases already linked.`)) return;
    try {
      await API.delete(`/admin/client-leads/${lead.id}`);
      toast.success("Lead removed");
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };
  const openPromote = (lead) => { setPromoteLead(lead); setPromotePassword(""); setPromoteDialogOpen(true); };
  const doPromote = async () => {
    if (!promotePassword || promotePassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setPromoting(true);
    try {
      const { data } = await API.post(`/admin/client-leads/${promoteLead.id}/promote`, { password: promotePassword });
      toast.success(`Lead promoted — ${data.user.email} can now log in`);
      setPromoteDialogOpen(false);
      fetchLeads();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Promote failed");
    } finally {
      setPromoting(false);
    }
  };

  const openCreateCaseFromLead = (lead) => {
    setCaseLead(lead);
    setCaseForm({
      title: `${lead.name} — ${lead.address || "Property"}`.slice(0, 80),
      property_address: lead.address || "",
      case_type: "buying",
    });
    setCaseDialogOpen(true);
  };
  const doCreateCaseFromLead = async () => {
    if (!caseForm.title.trim() || !caseForm.property_address.trim()) {
      toast.error("Case title and property address are required");
      return;
    }
    setCreatingCase(true);
    try {
      const { data } = await API.post("/cases", {
        title: caseForm.title.trim(),
        property_address: caseForm.property_address.trim(),
        case_type: caseForm.case_type,
        lead_id: caseLead.id,
      });
      toast.success("Case opened for this lead");
      setCaseDialogOpen(false);
      fetchLeads();
      navigate(`/cases/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to open case");
    } finally {
      setCreatingCase(false);
    }
  };

  // When the from-solicitor changes, clear case selection
  useEffect(() => { setSelectedCaseIds(new Set()); }, [selectedFromSolicitor]);

  const fromSolicitor = caseload.find((s) => s.id === selectedFromSolicitor) || null;
  const fromCases = fromSolicitor?.cases || [];
  const allActiveSelected = fromCases.length > 0 && fromCases.filter((c) => c.status === "active").every((c) => selectedCaseIds.has(c.id));

  const toggleCase = (id) => {
    const next = new Set(selectedCaseIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedCaseIds(next);
  };
  const selectAllActive = () => {
    if (allActiveSelected) { setSelectedCaseIds(new Set()); return; }
    setSelectedCaseIds(new Set(fromCases.filter((c) => c.status === "active").map((c) => c.id)));
  };

  const openReassign = () => {
    if (selectedCaseIds.size === 0) { toast.error("Select at least one case"); return; }
    setDestSolicitor("");
    setDisableCoverForSource(false);
    setReassignOpen(true);
  };

  const confirmReassign = async () => {
    if (!destSolicitor) { toast.error("Pick a destination solicitor"); return; }
    setBusy(true);
    try {
      const { data } = await API.post("/admin/cases/reassign", {
        case_ids: Array.from(selectedCaseIds),
        to_solicitor_id: destSolicitor,
        disable_cover_for_source: disableCoverForSource,
      });
      const coverNote = (data.cover_disabled_for || []).length > 0
        ? ` · cover routing cleared on ${data.cover_disabled_for.length} solicitor${data.cover_disabled_for.length === 1 ? "" : "s"}`
        : "";
      toast.success(`${data.moved} case${data.moved === 1 ? "" : "s"} reassigned to ${data.to_solicitor.name}${coverNote}`);
      setReassignOpen(false);
      setSelectedCaseIds(new Set());
      fetchCaseload();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reassignment failed");
    } finally {
      setBusy(false);
    }
  };

  const filteredUsers = users.filter((u) => u.role === tab);

  const openCreate = (role) => { setCreateForm({ ...empty, role }); setCreateOpen(true); };

  const handleCreate = async () => {
    if (!createForm.email || !createForm.password || !createForm.name) {
      toast.error("Email, password and name are required");
      return;
    }
    setBusy(true);
    try {
      await API.post("/admin/users", createForm);
      toast.success(`${createForm.role === "solicitor" ? "Solicitor" : "Client"} created`);
      setCreateOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setEditForm({
      name: u.name || "",
      email: u.email || "",
      phone: u.phone || "",
      office_address: u.office_address || "",
      is_admin: !!u.is_admin,
      out_of_office_enabled: !!u.out_of_office_enabled,
      cover_solicitor_id: u.cover_solicitor_id || "",
      user_type: u.user_type || "solicitor",
    });
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!editTarget) return;
    setBusy(true);
    try {
      const payload = { ...editForm };
      // Only send is_admin/cover/user_type fields for solicitors
      if (editTarget.role !== "solicitor") {
        delete payload.is_admin;
        delete payload.out_of_office_enabled;
        delete payload.cover_solicitor_id;
        delete payload.user_type;
      } else {
        // Normalise: send empty cover_solicitor_id as "" so backend clears it
        payload.cover_solicitor_id = (editForm.cover_solicitor_id || "").trim();
        // If OOO is on but no cover picked, block save with a toast
        if (payload.out_of_office_enabled && !payload.cover_solicitor_id) {
          toast.error("Pick a cover solicitor before enabling out-of-office");
          setBusy(false);
          return;
        }
      }
      await API.put(`/admin/users/${editTarget.id}`, payload);
      toast.success("User updated");
      setEditOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    } finally {
      setBusy(false);
    }
  };

  const openPasswordReset = (u) => { setPwTarget(u); setPwValue(""); setPwOpen(true); };

  const handlePasswordReset = async () => {
    if (!pwTarget) return;
    if (!pwValue || pwValue.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await API.put(`/admin/users/${pwTarget.id}/password`, { password: pwValue });
      toast.success(`Password reset for ${pwTarget.email}`);
      setPwOpen(false);
      setPwValue("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setBusy(false);
    }
  };

  const handleToggleActive = async (u) => {
    if (!window.confirm(`${u.is_active ? "Deactivate" : "Reactivate"} ${u.name} (${u.email})?`)) return;
    try {
      await API.put(`/admin/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Account deactivated" : "Account reactivated");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const handleToggleAdmin = async (u) => {
    if (!window.confirm(`${u.is_admin ? "Demote" : "Promote"} ${u.name} ${u.is_admin ? "to standard solicitor" : "to admin"}?`)) return;
    try {
      await API.put(`/admin/users/${u.id}`, { is_admin: !u.is_admin });
      toast.success(u.is_admin ? "Demoted to standard" : "Promoted to admin");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  // Permanent delete — opens a typed-confirmation dialog before firing
  // the DELETE request. Backend blocks delete if the user still has
  // cases assigned (solicitor) or is on any case (client).
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const openDelete = (u) => {
    setDeleteTarget(u);
    setDeleteConfirmText("");
  };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteBusy(true);
    try {
      await API.delete(`/admin/users/${target.id}`, {
        params: { confirm_email: target.email },
      });
      toast.success(`Deleted ${target.email}`);
      setDeleteTarget(null);
      setDeleteConfirmText("");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeleteBusy(false);
    }
  };

  if (!me?.is_admin) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-users-page">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              <Shield weight="fill" className="w-6 h-6 shrink-0" style={{ color: "#100f5d" }} />
              <span className="truncate">User Management</span>
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">{users.length} user{users.length === 1 ? "" : "s"} · {users.filter(u => u.is_admin).length} admin{users.filter(u => u.is_admin).length === 1 ? "" : "s"}</p>
          </div>
          <Button onClick={fetchUsers} variant="outline" size="sm" className="h-9 text-xs gap-1.5 self-start sm:self-auto shrink-0" data-testid="refresh-users-btn">
            <ArrowClockwise weight="bold" className="w-3.5 h-3.5" /> Refresh
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <TabsList className="bg-white border border-slate-200 w-full sm:w-auto grid grid-cols-2 sm:flex h-auto">
              <TabsTrigger value="solicitor" data-testid="tab-solicitor" className="gap-1.5 text-xs sm:text-sm"><Briefcase weight="bold" className="w-3.5 h-3.5" /><span className="truncate">Solicitors ({users.filter(u => u.role === "solicitor").length})</span></TabsTrigger>
              <TabsTrigger value="client" data-testid="tab-client" className="gap-1.5 text-xs sm:text-sm"><User weight="bold" className="w-3.5 h-3.5" /><span className="truncate">Clients ({users.filter(u => u.role === "client").length})</span></TabsTrigger>
              <TabsTrigger value="leads" data-testid="tab-leads" className="gap-1.5 text-xs sm:text-sm"><AddressBook weight="bold" className="w-3.5 h-3.5" /><span className="truncate">Leads ({leads.length})</span></TabsTrigger>
              <TabsTrigger value="caseload" data-testid="tab-caseload" className="gap-1.5 text-xs sm:text-sm"><FolderSimple weight="bold" className="w-3.5 h-3.5" /><span className="truncate">Caseload</span></TabsTrigger>
            </TabsList>
            {tab !== "caseload" && tab !== "leads" && (
              <Button onClick={() => openCreate(tab)} className="h-9 text-xs gap-1.5 text-white hover:opacity-90" style={{ background: "#100f5d" }} data-testid="create-user-btn">
                <UserPlus weight="bold" className="w-3.5 h-3.5" /> New {tab === "solicitor" ? "Solicitor" : "Client"}
              </Button>
            )}
            {tab === "leads" && (
              <Button onClick={openCreateLead} className="h-9 text-xs gap-1.5 text-white hover:opacity-90" style={{ background: "#100f5d" }} data-testid="create-lead-btn">
                <UserPlus weight="bold" className="w-3.5 h-3.5" /> New Client Lead
              </Button>
            )}
          </div>

          {["solicitor", "client"].map((roleKey) => (
            <TabsContent key={roleKey} value={roleKey} className="mt-0">
              <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
                {loading || (roleKey === "client" && clientsLoading) ? (
                  <div className="py-12 text-center text-sm text-slate-400">Loading…</div>
                ) : roleKey === "client" ? (
                  // ===== CLIENT ROLE — enriched view with phone + cases + profile view =====
                  clientsList.length === 0 ? (
                    <div className="py-12 text-center text-sm text-slate-400">No clients yet.</div>
                  ) : (
                  <table className="w-full text-sm" data-testid="clients-table">
                    <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                      <tr>
                        <th className="text-left px-4 py-3">Name</th>
                        <th className="text-left px-4 py-3">Email</th>
                        <th className="text-left px-4 py-3">Phone</th>
                        <th className="text-left px-4 py-3">Cases</th>
                        <th className="text-left px-4 py-3">Status</th>
                        <th className="text-right px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {clientsList.map((u) => {
                        const matchingUser = users.find((x) => x.id === u.id);
                        return (
                        <tr key={u.id} className="hover:bg-slate-50/50" data-testid={`client-row-${u.id}`}>
                          <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                          <td className="px-4 py-3 text-slate-600">{u.email}</td>
                          <td className="px-4 py-3 text-slate-600">{u.phone || <span className="text-slate-300">—</span>}</td>
                          <td className="px-4 py-3">
                            {u.cases_count > 0 ? (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors" data-testid={`client-cases-pill-${u.id}`}>
                                    {u.cases_count} {u.cases_count === 1 ? "case" : "cases"}
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-72 p-2">
                                  <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1 px-2">Linked cases</p>
                                  <ul className="space-y-1 max-h-60 overflow-y-auto">
                                    {u.cases.map((c) => (
                                      <li key={c.id}>
                                        <button onClick={() => navigate(`/cases/${c.id}`)} className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 transition-colors" data-testid={`client-case-jump-${c.id}`}>
                                          <p className="text-xs font-medium text-slate-900 truncate">{c.title}</p>
                                          <p className="text-[10px] text-slate-500 truncate">{c.property_address || "—"} · {c.status}</p>
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                </PopoverContent>
                              </Popover>
                            ) : (
                              <span className="text-slate-300 text-xs">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {u.is_active === false ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-700">Deactivated</span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700">Active</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <Button onClick={() => setViewClientId(u.id)} variant="ghost" size="sm" className="h-8 px-2 text-xs gap-1" data-testid={`view-client-profile-${u.id}`}>
                                <Eye weight="bold" className="w-3.5 h-3.5" /> View
                              </Button>
                              {matchingUser && (
                                <>
                                  <Button onClick={() => openEdit(matchingUser)} variant="ghost" size="sm" className="h-8 px-2 text-xs gap-1" data-testid={`edit-user-${u.id}`}>
                                    <Pencil weight="bold" className="w-3.5 h-3.5" /> Edit
                                  </Button>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`user-actions-menu-${u.id}`}>
                                        <DotsThreeVertical weight="bold" className="w-4 h-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-44">
                                      <DropdownMenuItem onClick={() => openPasswordReset(matchingUser)} data-testid={`reset-password-${u.id}`}>
                                        <Key weight="bold" className="w-3.5 h-3.5 mr-2" /> Reset password
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleToggleActive(matchingUser)} data-testid={`toggle-active-${u.id}`}
                                        className={u.is_active === false ? "text-emerald-700 focus:text-emerald-800" : "text-red-600 focus:text-red-700"}>
                                        <Power weight="bold" className="w-3.5 h-3.5 mr-2" /> {u.is_active === false ? "Reactivate" : "Deactivate"}
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem onClick={() => openDelete(matchingUser)} className="text-red-700 focus:text-red-800 focus:bg-red-50" data-testid={`delete-user-${u.id}`}>
                                        <Trash weight="bold" className="w-3.5 h-3.5 mr-2" /> Delete permanently
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  )
                ) : filteredUsers.length === 0 ? (
                  <div className="py-12 text-center text-sm text-slate-400">No {roleKey}s yet.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                      <tr>
                        <th className="text-left px-4 py-3">Name</th>
                        <th className="text-left px-4 py-3">Email</th>
                        {roleKey === "solicitor" && <th className="text-left px-4 py-3">Type</th>}
                        {roleKey === "solicitor" && <th className="text-left px-4 py-3">Role</th>}
                        <th className="text-left px-4 py-3">Status</th>
                        <th className="text-right px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredUsers.map((u) => {
                        const isMe = me?.id === u.id;
                        return (
                          <tr key={u.id} className="hover:bg-slate-50/50" data-testid={`user-row-${u.id}`}>
                            <td className="px-4 py-3 font-medium text-slate-900">
                              {u.name}
                              {isMe && <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-400">(you)</span>}
                            </td>
                            <td className="px-4 py-3 text-slate-600">{u.email}</td>
                            {roleKey === "solicitor" && (
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${userTypeColor(u.user_type)}`} data-testid={`user-type-pill-${u.id}`}>
                                  {userTypeLabel(u.user_type)}
                                </span>
                              </td>
                            )}
                            {roleKey === "solicitor" && (
                              <td className="px-4 py-3">
                                {u.is_admin ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold" style={{ background: "rgba(200,125,58,0.12)", color: "#c87d3a" }}>
                                    <Shield weight="fill" className="w-3 h-3" /> Admin
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600">Standard</span>
                                )}
                              </td>
                            )}
                            <td className="px-4 py-3">
                              {u.is_active === false ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-700">Deactivated</span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700">Active</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-1">
                                <Button onClick={() => openEdit(u)} variant="ghost" size="sm" className="h-8 px-2 text-xs gap-1" data-testid={`edit-user-${u.id}`}>
                                  <Pencil weight="bold" className="w-3.5 h-3.5" /> Edit
                                </Button>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`user-actions-menu-${u.id}`}>
                                      <DotsThreeVertical weight="bold" className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-44">
                                    <DropdownMenuItem onClick={() => openPasswordReset(u)} data-testid={`reset-password-${u.id}`}>
                                      <Key weight="bold" className="w-3.5 h-3.5 mr-2" /> Reset password
                                    </DropdownMenuItem>
                                    {roleKey === "solicitor" && !isMe && (
                                      <DropdownMenuItem onClick={() => handleToggleAdmin(u)} data-testid={`toggle-admin-${u.id}`}>
                                        <Shield weight="bold" className="w-3.5 h-3.5 mr-2" /> {u.is_admin ? "Demote to standard" : "Promote to admin"}
                                      </DropdownMenuItem>
                                    )}
                                    {!isMe && (
                                      <DropdownMenuItem onClick={() => handleToggleActive(u)} data-testid={`toggle-active-${u.id}`}
                                        className={u.is_active === false ? "text-emerald-700 focus:text-emerald-800" : "text-red-600 focus:text-red-700"}>
                                        <Power weight="bold" className="w-3.5 h-3.5 mr-2" /> {u.is_active === false ? "Reactivate" : "Deactivate"}
                                      </DropdownMenuItem>
                                    )}
                                    {!isMe && <DropdownMenuSeparator />}
                                    {!isMe && (
                                      <DropdownMenuItem onClick={() => openDelete(u)} className="text-red-700 focus:text-red-800 focus:bg-red-50" data-testid={`delete-user-${u.id}`}>
                                        <Trash weight="bold" className="w-3.5 h-3.5 mr-2" /> Delete permanently
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </TabsContent>
          ))}

          {/* Caseload tab — bulk reassignment */}
          <TabsContent value="caseload" className="mt-0">
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 space-y-4" data-testid="caseload-section">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Bulk case reassignment</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Pick the source solicitor, tick which cases to move, then reassign to another solicitor in one click. Notifies the old solicitor, the new solicitor, and every linked client.</p>
                </div>
                <Button onClick={fetchCaseload} variant="outline" size="sm" className="h-9 text-xs gap-1.5" data-testid="refresh-caseload-btn">
                  <ArrowClockwise weight="bold" className="w-3.5 h-3.5" /> Refresh
                </Button>
              </div>

              {caseloadLoading ? (
                <div className="py-12 text-center text-sm text-slate-400">Loading caseload…</div>
              ) : (
                <>
                  {/* Per-solicitor summary cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {caseload.map((s) => {
                      const isSelected = selectedFromSolicitor === s.id;
                      return (
                        <button
                          key={s.id}
                          onClick={() => setSelectedFromSolicitor(s.id)}
                          data-testid={`caseload-solicitor-${s.id}`}
                          className={`text-left p-3 rounded-lg border transition-all ${isSelected ? "border-[#100f5d] shadow-md ring-2 ring-[#100f5d]/10" : "border-slate-200 hover:border-slate-300"} ${s.is_active === false ? "opacity-60" : ""}`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Briefcase weight="bold" className="w-3.5 h-3.5" style={{ color: "#100f5d" }} />
                            <p className="text-sm font-semibold text-slate-900 truncate">{s.name || "—"}</p>
                            {s.is_admin && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ background: "rgba(200,125,58,0.12)", color: "#c87d3a" }}>Admin</span>}
                            {s.is_active === false && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-50 text-red-700">Off</span>}
                          </div>
                          <p className="text-[11px] text-slate-500 truncate">{s.email}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs">
                            <span className="font-bold" style={{ color: "#100f5d" }}>{s.active_cases}</span>
                            <span className="text-slate-400">active</span>
                            <span className="text-slate-300">·</span>
                            <span className="text-slate-500">{s.total_cases} total</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Cases table for selected source solicitor */}
                  {fromSolicitor && (
                    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="caseload-cases-table">
                      <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap" style={{ background: "rgba(16,15,93,0.04)" }}>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{fromSolicitor.name}'s cases</p>
                          <p className="text-[11px] text-slate-500">{selectedCaseIds.size} selected of {fromCases.length}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button onClick={selectAllActive} variant="outline" size="sm" className="h-8 text-xs" data-testid="select-all-active-btn">
                            {allActiveSelected ? "Deselect all" : "Select all active"}
                          </Button>
                          <Button onClick={openReassign} disabled={selectedCaseIds.size === 0} className="h-8 text-xs gap-1.5 text-white hover:opacity-90" style={{ background: "#100f5d" }} data-testid="open-reassign-btn">
                            <ArrowsClockwise weight="bold" className="w-3.5 h-3.5" /> Reassign {selectedCaseIds.size > 0 ? selectedCaseIds.size : ""}
                          </Button>
                        </div>
                      </div>
                      {fromCases.length === 0 ? (
                        <div className="py-8 text-center text-xs text-slate-400">No cases under this solicitor.</div>
                      ) : (
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                            <tr>
                              <th className="text-left px-4 py-2 w-8"></th>
                              <th className="text-left px-4 py-2">Case</th>
                              <th className="text-left px-4 py-2">Client</th>
                              <th className="text-left px-4 py-2">Type</th>
                              <th className="text-left px-4 py-2">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {fromCases.map((c) => (
                              <tr key={c.id} className={`hover:bg-slate-50/60 cursor-pointer ${selectedCaseIds.has(c.id) ? "bg-[#100f5d]/5" : ""}`} onClick={() => toggleCase(c.id)} data-testid={`caseload-case-row-${c.id}`}>
                                <td className="px-4 py-2">
                                  <input type="checkbox" checked={selectedCaseIds.has(c.id)} onChange={() => toggleCase(c.id)} onClick={(e) => e.stopPropagation()} className="rounded border-slate-300" data-testid={`caseload-case-cb-${c.id}`} />
                                </td>
                                <td className="px-4 py-2">
                                  <p className="text-sm font-medium text-slate-900 truncate max-w-[280px]">{c.title}</p>
                                  {c.property_address && <p className="text-[11px] text-slate-500 truncate max-w-[280px]">{c.property_address}</p>}
                                </td>
                                <td className="px-4 py-2 text-slate-600">{c.client_name || "—"}</td>
                                <td className="px-4 py-2 text-slate-500 capitalize">{c.case_type || "—"}</td>
                                <td className="px-4 py-2">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${c.status === "completed" ? "bg-emerald-50 text-emerald-700" : c.status === "active" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"}`}>{c.status}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                  {!fromSolicitor && (
                    <div className="py-8 text-center text-xs text-slate-400 border border-dashed border-slate-200 rounded-lg">
                      Pick a solicitor above to view their caseload.
                    </div>
                  )}
                </>
              )}
            </div>
          </TabsContent>

          {/* Client Leads tab — contact-only records */}
          <TabsContent value="leads" className="mt-0">
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
              {leadsLoading ? (
                <div className="px-4 py-8 text-center text-xs text-slate-400">Loading…</div>
              ) : leads.length === 0 ? (
                <div className="px-4 py-12 text-center text-xs text-slate-400">
                  No client leads yet. Use this list to capture clients you've spoken to before they sign up.
                </div>
              ) : (
                <table className="w-full text-xs" data-testid="leads-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                      <th className="px-4 py-2.5">Name</th>
                      <th className="px-4 py-2.5">Email</th>
                      <th className="px-4 py-2.5">Phone</th>
                      <th className="px-4 py-2.5">Address</th>
                      <th className="px-4 py-2.5 text-center">Cases</th>
                      <th className="px-4 py-2.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((l) => (
                      <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`lead-row-${l.id}`}>
                        <td className="px-4 py-2.5 font-medium text-slate-800">{l.name}</td>
                        <td className="px-4 py-2.5 text-slate-600">{l.email || <span className="text-slate-300">—</span>}</td>
                        <td className="px-4 py-2.5 text-slate-600">{l.phone || <span className="text-slate-300">—</span>}</td>
                        <td className="px-4 py-2.5 text-slate-600">{l.address || <span className="text-slate-300">—</span>}</td>
                        <td className="px-4 py-2.5 text-center">
                          {l.cases_count > 0 ? (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button data-testid={`lead-cases-badge-${l.id}`} className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full bg-blue-50 text-blue-700 text-[11px] font-semibold border border-blue-200 hover:bg-blue-100 transition-colors">
                                  {l.cases_count}
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-72 p-2" align="end">
                                <div className="text-[10px] uppercase tracking-wide text-slate-400 px-2 py-1">Linked cases</div>
                                <div className="space-y-0.5">
                                  {l.cases.map((c) => (
                                    <button key={c.id} type="button"
                                      onClick={() => navigate(`/cases/${c.id}`)}
                                      data-testid={`lead-case-link-${c.id}`}
                                      className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-50 flex items-center justify-between gap-2">
                                      <span className="text-xs text-slate-800 truncate">{c.title || c.property_address || c.id.slice(0, 8)}</span>
                                      <span className="text-[10px] text-slate-400 capitalize shrink-0">{c.status}</span>
                                    </button>
                                  ))}
                                </div>
                              </PopoverContent>
                            </Popover>
                          ) : (
                            <span className="text-slate-300 text-[11px]">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" className="h-7 text-[11px] gap-1" onClick={() => openEditLead(l)} data-testid={`edit-lead-${l.id}`}>
                              <Pencil weight="bold" className="w-3 h-3" /> Edit
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-[11px] gap-1 text-blue-700 hover:text-blue-800 hover:bg-blue-50" onClick={() => openCreateCaseFromLead(l)} data-testid={`create-case-from-lead-${l.id}`} title="Open a new case for this lead">
                              <FolderPlus weight="bold" className="w-3 h-3" /> Open Case
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-[11px] gap-1 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50" onClick={() => openPromote(l)} disabled={!l.email} data-testid={`promote-lead-${l.id}`} title={!l.email ? "Add an email before promoting" : "Convert to a real client account"}>
                              <Sparkle weight="bold" className="w-3 h-3" /> Promote
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-[11px] gap-1 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => deleteLead(l)} data-testid={`delete-lead-${l.id}`}>
                              <Trash weight="bold" className="w-3 h-3" /> Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Reassign confirm dialog */}
      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent className="max-w-md" data-testid="reassign-dialog">
          <DialogHeader>
            <DialogTitle>Reassign {selectedCaseIds.size} case{selectedCaseIds.size === 1 ? "" : "s"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Moving {selectedCaseIds.size} case{selectedCaseIds.size === 1 ? "" : "s"} from <span className="font-semibold text-slate-700">{fromSolicitor?.name}</span> to a new solicitor. The old + new solicitor and every linked client will be notified.
            </p>
            <div>
              <Label className="text-xs">Destination solicitor</Label>
              <Select value={destSolicitor} onValueChange={setDestSolicitor}>
                <SelectTrigger data-testid="reassign-dest-select" className="h-9 text-sm">
                  <SelectValue placeholder="Pick a solicitor…" />
                </SelectTrigger>
                <SelectContent>
                  {caseload.filter((s) => s.id !== selectedFromSolicitor && s.is_active !== false).map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`reassign-dest-${s.id}`}>
                      {s.name} {s.is_admin ? "· Admin" : ""}  ({s.active_cases} active)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-start gap-2 text-xs cursor-pointer pt-1">
              <input
                type="checkbox"
                data-testid="reassign-disable-cover-cb"
                checked={disableCoverForSource}
                onChange={(e) => setDisableCoverForSource(e.target.checked)}
                className="rounded border-slate-300 mt-0.5"
              />
              <span className="text-slate-600 leading-snug">
                Also turn off out-of-office cover routing on <span className="font-semibold text-slate-700">{fromSolicitor?.name || "the source solicitor"}</span> (clears their cover_solicitor_id). Use this when permanently moving the caseload away.
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={confirmReassign} disabled={busy || !destSolicitor} className="text-white hover:opacity-90" style={{ background: "#100f5d" }} data-testid="reassign-confirm-btn">
              {busy ? "Reassigning…" : "Confirm reassignment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle>New {createForm.role === "solicitor" ? "Solicitor" : "Client"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Full name</Label>
              <Input data-testid="create-name" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input data-testid="create-email" type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Initial password (min 6 chars)</Label>
              <Input data-testid="create-password" type="text" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} placeholder="They can change it later from /settings" />
            </div>
            {createForm.role === "solicitor" && (
              <>
                <div>
                  <Label className="text-xs">User type</Label>
                  <Select value={createForm.user_type} onValueChange={(v) => setCreateForm({ ...createForm, user_type: v })}>
                    <SelectTrigger data-testid="create-user-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {USER_TYPE_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-400 mt-1">All types share the same access — this is just for org reporting.</p>
                </div>
                <div>
                  <Label className="text-xs">Phone (optional)</Label>
                  <Input data-testid="create-phone" value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Office address (optional)</Label>
                  <Input data-testid="create-office" value={createForm.office_address} onChange={(e) => setCreateForm({ ...createForm, office_address: e.target.value })} />
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer pt-1">
                  <input type="checkbox" data-testid="create-is-admin" checked={createForm.is_admin} onChange={(e) => setCreateForm({ ...createForm, is_admin: e.target.checked })} className="rounded border-slate-300" />
                  <span>Grant <span className="font-semibold" style={{ color: "#c87d3a" }}>Admin</span> privileges</span>
                </label>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={handleCreate} disabled={busy} className="text-white hover:opacity-90" style={{ background: "#100f5d" }} data-testid="create-confirm-btn">
              {busy ? "Creating…" : "Create user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit {editTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Full name</Label>
              <Input data-testid="edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input data-testid="edit-email" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </div>
            {editTarget?.role === "solicitor" && (
              <>
                <div>
                  <Label className="text-xs">User type</Label>
                  <Select value={editForm.user_type} onValueChange={(v) => setEditForm({ ...editForm, user_type: v })}>
                    <SelectTrigger data-testid="edit-user-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {USER_TYPE_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input data-testid="edit-phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Office address</Label>
                  <Input data-testid="edit-office" value={editForm.office_address} onChange={(e) => setEditForm({ ...editForm, office_address: e.target.value })} />
                </div>
                {editTarget?.id !== me?.id && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer pt-1">
                    <input type="checkbox" data-testid="edit-is-admin" checked={editForm.is_admin} onChange={(e) => setEditForm({ ...editForm, is_admin: e.target.checked })} className="rounded border-slate-300" />
                    <span>Admin privileges</span>
                  </label>
                )}

                {/* Out-of-office cover routing */}
                <div className="border-t border-slate-100 pt-3 space-y-2">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Out-of-office cover routing</p>
                  <p className="text-[11px] text-slate-400 leading-snug">When enabled, this solicitor's incoming notifications and unread-message digests are redirected to the chosen cover solicitor.</p>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="edit-out-of-office"
                      checked={!!editForm.out_of_office_enabled}
                      onChange={(e) => setEditForm({ ...editForm, out_of_office_enabled: e.target.checked })}
                      className="rounded border-slate-300"
                    />
                    <span>Out-of-office enabled (redirect to cover)</span>
                  </label>
                  <div>
                    <Label className="text-xs">Cover solicitor</Label>
                    <Select
                      value={editForm.cover_solicitor_id || "__none__"}
                      onValueChange={(v) => setEditForm({ ...editForm, cover_solicitor_id: v === "__none__" ? "" : v })}
                    >
                      <SelectTrigger data-testid="edit-cover-solicitor-select" className="h-9 text-sm">
                        <SelectValue placeholder="No cover assigned" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__" data-testid="edit-cover-none">— No cover —</SelectItem>
                        {users
                          .filter((s) => s.role === "solicitor" && s.id !== editTarget?.id && s.is_active !== false)
                          .map((s) => (
                            <SelectItem key={s.id} value={s.id} data-testid={`edit-cover-option-${s.id}`}>
                              {s.name} {s.is_admin ? "· Admin" : ""}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={handleEdit} disabled={busy} className="text-white hover:opacity-90" style={{ background: "#100f5d" }} data-testid="edit-confirm-btn">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="max-w-sm" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset password for {pwTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Sets a new password immediately. The user is not auto-emailed — share the new password with them via your usual secure channel.</p>
            <div>
              <Label className="text-xs">New password (min 6 chars)</Label>
              <Input data-testid="reset-pw-input" type="text" value={pwValue} onChange={(e) => setPwValue(e.target.value)} placeholder="Enter a new password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={handlePasswordReset} disabled={busy} className="text-white hover:opacity-90" style={{ background: "#100f5d" }} data-testid="reset-pw-confirm-btn">
              {busy ? "Resetting…" : "Reset password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Lead create / edit dialog */}
      <Dialog open={leadDialogOpen} onOpenChange={setLeadDialogOpen}>
        <DialogContent className="max-w-md" data-testid="lead-dialog">
          <DialogHeader>
            <DialogTitle>{editingLeadId ? "Edit Client Lead" : "New Client Lead"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">A contact-only record for clients you've spoken to but who haven't signed up yet. They'll get a real login when you Promote.</p>
            <div className="space-y-1.5">
              <Label className="text-xs">Name *</Label>
              <Input data-testid="lead-name-input" value={leadForm.name} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })} placeholder="Jane Doe" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input data-testid="lead-email-input" type="email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} placeholder="jane@example.com" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Phone</Label>
                <Input data-testid="lead-phone-input" value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} placeholder="07700 …" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Address</Label>
                <Input data-testid="lead-address-input" value={leadForm.address} onChange={(e) => setLeadForm({ ...leadForm, address: e.target.value })} placeholder="5 Oak Lane, …" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Notes</Label>
              <Input data-testid="lead-notes-input" value={leadForm.notes} onChange={(e) => setLeadForm({ ...leadForm, notes: e.target.value })} placeholder="e.g. Spoke 12 May, eager to instruct" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeadDialogOpen(false)} disabled={savingLead}>Cancel</Button>
            <Button onClick={saveLead} disabled={savingLead} className="text-white hover:opacity-90" style={{ background: "#100f5d" }} data-testid="save-lead-btn">
              {savingLead ? "Saving…" : (editingLeadId ? "Save changes" : "Add lead")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promote lead → real client account */}
      <Dialog open={promoteDialogOpen} onOpenChange={setPromoteDialogOpen}>
        <DialogContent className="max-w-md" data-testid="promote-lead-dialog">
          <DialogHeader>
            <DialogTitle>Promote to Client Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-600">
              <span className="font-semibold">{promoteLead?.name}</span> ({promoteLead?.email}) will get a real client account with the password you set below. Share the password with them so they can log in.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs">Initial password (min 6 chars)</Label>
              <Input data-testid="promote-password-input" type="text" value={promotePassword} onChange={(e) => setPromotePassword(e.target.value)} placeholder="TempPass123!" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoteDialogOpen(false)} disabled={promoting}>Cancel</Button>
            <Button onClick={doPromote} disabled={promoting} className="text-white hover:opacity-90 gap-1.5" style={{ background: "#100f5d" }} data-testid="promote-confirm-btn">
              <Sparkle weight="bold" className="w-3.5 h-3.5" />
              {promoting ? "Promoting…" : "Promote to client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Open a new case for a lead */}
      <Dialog open={caseDialogOpen} onOpenChange={setCaseDialogOpen}>
        <DialogContent className="max-w-md" data-testid="create-case-from-lead-dialog">
          <DialogHeader>
            <DialogTitle>Open Case for {caseLead?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              The lead's name, email and phone are copied onto the new case automatically. The lead stays in the leads list until you Promote them.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs">Case title *</Label>
              <Input data-testid="case-title-input" value={caseForm.title} onChange={(e) => setCaseForm({ ...caseForm, title: e.target.value })} placeholder="Jane Doe — 5 Oak Lane" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Property address *</Label>
              <Input data-testid="case-property-input" value={caseForm.property_address} onChange={(e) => setCaseForm({ ...caseForm, property_address: e.target.value })} placeholder="5 Oak Lane, Norwich, NR1 1AA" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Case type</Label>
              <Select value={caseForm.case_type} onValueChange={(v) => setCaseForm({ ...caseForm, case_type: v })}>
                <SelectTrigger data-testid="case-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="buying">Buying</SelectItem>
                  <SelectItem value="selling">Selling</SelectItem>
                  <SelectItem value="remortgage">Remortgage</SelectItem>
                  <SelectItem value="transfer">Transfer of Equity</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCaseDialogOpen(false)} disabled={creatingCase}>Cancel</Button>
            <Button onClick={doCreateCaseFromLead} disabled={creatingCase} className="text-white hover:opacity-90 gap-1.5" style={{ background: "#100f5d" }} data-testid="create-case-confirm-btn">
              <FolderPlus weight="bold" className="w-3.5 h-3.5" />
              {creatingCase ? "Opening…" : "Open case"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Read-only client profile drawer — surfaces master account + every
          linked case with its per-case extended details (DOB/AML/etc.). */}
      <ClientProfileDialog
        open={!!viewClientId}
        onOpenChange={(open) => !open && setViewClientId(null)}
        clientId={viewClientId}
      />

      {/* Permanent-delete confirmation dialog.

          Typed-email confirmation matches the pattern users expect from
          GitHub / Stripe etc. for irreversible destructive actions. The
          backend independently re-validates the email so a fast-click in
          devtools can't bypass this. */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteConfirmText(""); } }}>
        <DialogContent className="max-w-md" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">Permanently delete user?</DialogTitle>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-3 py-2">
              <div className="bg-red-50 border border-red-200 rounded-md p-3 text-xs text-red-800">
                <p className="font-semibold mb-1">This is irreversible.</p>
                <ul className="list-disc list-inside space-y-0.5 text-red-700">
                  <li>The user row will be removed from the database.</li>
                  <li>Outlook tokens, cached emails, and login credentials will be wiped.</li>
                  <li>Quotes and audit history they created stay (compliance).</li>
                  <li>If they own any cases, deletion will be blocked — reassign first.</li>
                </ul>
              </div>
              <div>
                <p className="text-sm text-slate-700">
                  You are about to delete{" "}
                  <span className="font-semibold">{deleteTarget.name || deleteTarget.email}</span>
                </p>
                <p className="text-xs text-slate-500 mt-0.5">{deleteTarget.email}</p>
              </div>
              <div>
                <Label htmlFor="delete-confirm" className="text-xs font-medium text-slate-700">
                  Type the email to confirm
                </Label>
                <Input
                  id="delete-confirm"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={deleteTarget.email}
                  autoComplete="off"
                  className="mt-1"
                  data-testid="delete-confirm-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setDeleteTarget(null); setDeleteConfirmText(""); }}
              disabled={deleteBusy}
              data-testid="delete-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={
                deleteBusy ||
                !deleteTarget ||
                deleteConfirmText.trim().toLowerCase() !== (deleteTarget?.email || "").toLowerCase()
              }
              className="bg-red-600 hover:bg-red-700 text-white gap-1.5"
              data-testid="delete-confirm-btn"
            >
              <Trash weight="bold" className="w-3.5 h-3.5" />
              {deleteBusy ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
